import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Play,
    CheckCircle2,
    Star,
    Sparkles,
    Brain,
    ShieldCheck,
    BarChart3,
    FileText,
    Languages,
    Target,
    AlertTriangle,
    Download,
    Coins,
    CalendarClock,
    Lock,
    Award,
    Users,
    Building2,
    Landmark,
    TrendingUp,
    BadgeCheck,
    Clock,
    DollarSign,
    Search,
    ClipboardCheck,
    Layers,
    Network,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { TESTIMONIALS, FAQS, PLANS } from "@/lib/mockData";
import DashboardPreview from "@/components/landing/DashboardPreview";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

/* ============================================================
   1. HERO
   ============================================================ */
const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

const HERO_PARTICLES = Array.from({ length: 14 }, (_, i) => ({
    id: `particle-${i}`,
    left: `${(i * 73) % 100}%`,
    top: `${(i * 41) % 90}%`,
    size: 2 + (i % 3),
    color: i % 3 === 0 ? "#D4AF37" : "#5EEAD4",
    duration: 4 + (i % 4),
    delay: i * 0.3,
}));

function Hero() {
    return (
        <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
            {/* layered background */}
            <div className="absolute inset-0 mesh-hero" />
            <div className="absolute inset-0 grid-pattern-dark opacity-50" />

            {/* floating particles */}
            {HERO_PARTICLES.map((p) => (
                <motion.div
                    key={p.id}
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        left: p.left,
                        top: p.top,
                        width: p.size,
                        height: p.size,
                        background: p.color,
                        opacity: 0.6,
                    }}
                    animate={{
                        y: [0, -20, 0],
                        opacity: [0.2, 0.8, 0.2],
                    }}
                    transition={{
                        duration: p.duration,
                        repeat: Infinity,
                        delay: p.delay,
                    }}
                />
            ))}

            <div className="container-tight relative grid lg:grid-cols-[1.05fr_1.1fr] gap-12 lg:gap-10 items-center">
                {/* LEFT */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="badge-pill border border-teal-500/30 bg-teal-500/10 text-teal-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
                        AI Tender Intelligence Platform
                    </div>

                    <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1] font-bold text-white uppercase tracking-tight text-balance">
                        Analyze{" "}
                        <span className="gradient-text-gold">Tenders</span>
                        <br />
                        in Seconds
                        <br />
                        with <span className="gradient-text-teal">AI</span>
                    </h1>

                    <div className="mt-6 flex items-center gap-3 text-gold">
                        <span className="h-px w-12 bg-gold opacity-60" />
                        <span className="h-2 w-2 rotate-45 border border-gold" />
                        <span className="h-px w-12 bg-gold opacity-60" />
                    </div>

                    <p className="mt-7 text-lg text-slate-300 leading-relaxed max-w-xl">
                        TenderTrackAI helps government contractors and private bidders
                        quickly analyze tender documents, assess compliance, evaluate
                        risks, and decide with confidence.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <Link to="/signup">
                            <button
                                className="luxury-btn px-7 h-14 rounded-2xl text-sm font-bold uppercase tracking-[0.15em] w-full sm:w-auto inline-flex items-center justify-center gap-3"
                                data-testid="hero-signup-btn"
                            >
                                Sign Up for Free
                                <span className="h-7 w-7 rounded-full bg-white/20 inline-flex items-center justify-center">
                                    <ArrowRight className="h-3.5 w-3.5" />
                                </span>
                            </button>
                        </Link>
                        <Link to="/dashboard/analysis/tta-2891">
                            <button
                                className="px-7 h-14 rounded-2xl text-sm font-bold uppercase tracking-[0.15em] border border-white/20 text-white bg-white/[0.03] hover:bg-white/[0.07] transition-all w-full sm:w-auto inline-flex items-center justify-center gap-3"
                                data-testid="hero-demo-btn"
                            >
                                <Play className="h-4 w-4 text-gold" />
                                Request a Demo Report
                            </button>
                        </Link>
                    </div>

                    <div className="mt-10 flex items-center gap-6">
                        <div className="flex -space-x-2">
                            {TESTIMONIALS.slice(0, 3).map((t) => (
                                <img
                                    key={t.name}
                                    src={t.image}
                                    alt={t.name}
                                    className="h-9 w-9 rounded-full border-2 border-[#071A2F] object-cover"
                                />
                            ))}
                        </div>
                        <div>
                            <div className="flex items-center gap-1">
                                {STAR_KEYS.map((s) => (
                                    <Star
                                        key={s}
                                        className="h-3.5 w-3.5 fill-gold text-gold"
                                    />
                                ))}
                                <span className="ml-1.5 text-xs font-mono text-slate-300">
                                    4.9 / 5
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                                Trusted by 1,200+ bid professionals
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* RIGHT — Dashboard mockup */}
                <DashboardPreview />
            </div>
        </section>
    );
}

/* ============================================================
   2. TRUSTED BY
   ============================================================ */
function TrustedBy() {
    const logos = [
        "NHAI",
        "PWD Rajasthan",
        "GeM Portal",
        "Indian Railways",
        "MES",
        "NTPC",
        "BHEL",
        "MoRTH",
        "ONGC",
        "CPPP",
    ];
    return (
        <section className="py-16 border-y border-white/5 bg-white/[0.015]">
            <div className="container-tight">
                <p className="text-center text-[11px] font-bold uppercase tracking-[0.32em] text-slate-400 mb-10">
                    Trusted by contractors bidding on tenders from
                </p>
                <div className="relative overflow-hidden">
                    <div className="flex gap-14 marquee whitespace-nowrap">
                        {[...logos, ...logos].map((l, i) => (
                            <span
                                key={`${l}-${i}`}
                                className="text-lg font-bold tracking-[0.18em] text-slate-500/80 hover:text-white transition-colors"
                            >
                                {l}
                            </span>
                        ))}
                    </div>
                    <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#071A2F] to-transparent pointer-events-none" />
                    <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#071A2F] to-transparent pointer-events-none" />
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   3. FEATURES BENTO GRID
   ============================================================ */
function FeaturesBento() {
    return (
        <section
            id="features"
            className="py-28 lg:py-36 relative"
        >
            <div className="absolute inset-0 mesh-hero opacity-40" />
            <div className="container-tight relative">
                <motion.div {...fadeUp} className="max-w-3xl">
                    <p className="badge-pill border border-gold/30 bg-gold/5 text-gold">
                        <Sparkles className="h-3 w-3" /> Capabilities
                    </p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                        Every signal you need,
                        <br />
                        <span className="gradient-text-teal">in one report.</span>
                    </h2>
                    <p className="mt-5 text-lg text-slate-300 max-w-2xl">
                        Ten precision tools that decode any tender document — eligibility,
                        risk, deadlines, EMD, and a multilingual summary, all in under a
                        minute.
                    </p>
                </motion.div>

                {/* Bento grid */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-5">
                    {/* large card */}
                    <BentoCard
                        className="md:col-span-3 md:row-span-2"
                        accent="teal"
                        size="lg"
                        icon={Brain}
                        title="AI Tender Summary"
                        desc="Our domain-tuned model condenses 80-page tenders into a structured, decision-ready summary you can scan in 90 seconds."
                        chip="GPT-grade extraction"
                    >
                        <div className="mt-6 rounded-2xl border border-teal-500/15 bg-[#0B2438] p-5">
                            <div className="flex items-center gap-2 text-[11px] font-mono text-teal-300 mb-3">
                                <span className="h-2 w-2 rounded-full bg-teal-400 animate-pulse" />
                                AI analyzing…
                            </div>
                            <div className="space-y-2">
                                {[
                                    "Extracting eligibility criteria",
                                    "Mapping financial requirements",
                                    "Flagging unfavorable clauses",
                                ].map((s, i) => (
                                    <motion.div
                                        key={s}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.15 }}
                                        className="flex items-center gap-2 text-sm text-slate-300"
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5 text-teal-400 shrink-0" />
                                        {s}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </BentoCard>

                    <BentoCard
                        className="md:col-span-3"
                        accent="gold"
                        icon={ShieldCheck}
                        title="Eligibility Analysis"
                        desc="Instantly know if your business qualifies — turnover, experience, licenses, all cross-checked."
                    />
                    <BentoCard
                        className="md:col-span-3"
                        accent="teal"
                        icon={AlertTriangle}
                        title="Risk Detection"
                        desc="Spot liquidated damages, performance security clauses, and penalty traps before they hurt you."
                    />

                    <BentoCard
                        className="md:col-span-2"
                        accent="teal"
                        icon={Target}
                        title="Match Score"
                        desc="0–100 fit score for every tender."
                    />
                    <BentoCard
                        className="md:col-span-2"
                        accent="gold"
                        icon={Coins}
                        title="EMD Detection"
                        desc="Amount, mode, and validity period."
                    />
                    <BentoCard
                        className="md:col-span-2"
                        accent="teal"
                        icon={CalendarClock}
                        title="Deadlines"
                        desc="Never miss a critical date again."
                    />

                    <BentoCard
                        className="md:col-span-3"
                        accent="gold"
                        icon={Languages}
                        title="All Language Summary"
                        desc="Read every clause in your preferred language — Hindi, Tamil, Bengali, Telugu and more — auto-translated by AI."
                    />
                    <BentoCard
                        className="md:col-span-3"
                        accent="teal"
                        icon={Download}
                        title="PDF Report Export"
                        desc="Beautifully formatted, board-ready PDF reports you can share with your team and partners."
                    />
                </div>
            </div>
        </section>
    );
}

function BentoCard({
    className = "",
    icon: Icon,
    title,
    desc,
    accent = "teal",
    chip,
    children,
    size = "md",
}) {
    const accentClasses =
        accent === "gold"
            ? "before:bg-[linear-gradient(135deg,rgba(212,175,55,0.5),rgba(212,175,55,0.05))]"
            : "before:bg-[linear-gradient(135deg,rgba(94,234,212,0.4),rgba(94,234,212,0.05))]";
    const iconBg =
        accent === "gold"
            ? "bg-gold/10 text-gold border-gold/30"
            : "bg-teal-500/10 text-teal-300 border-teal-500/30";
    return (
        <motion.div
            {...fadeUp}
            className={`group relative rounded-3xl p-7 lg:p-8 bg-[#0B2238]/60 border border-white/[0.06] hover:border-white/[0.14] transition-all hover:-translate-y-1 ${className}`}
        >
            <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${iconBg} mb-5 group-hover:scale-110 transition-transform`}>
                <Icon className="h-5 w-5" />
            </div>
            {chip && (
                <span className="absolute top-7 right-7 text-[10px] font-mono uppercase tracking-wider text-slate-400 border border-white/10 rounded-full px-2 py-0.5">
                    {chip}
                </span>
            )}
            <h3
                className={`font-serif font-bold text-white ${size === "lg" ? "text-2xl lg:text-3xl" : "text-xl"}`}
            >
                {title}
            </h3>
            <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{desc}</p>
            {children}
            {/* accent corner glow */}
            <div className={`absolute -inset-px rounded-3xl ${accentClasses} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -z-10`} />
        </motion.div>
    );
}

/* ============================================================
   4. AI TENDER ANALYSIS DEMO
   ============================================================ */
function AIDemo() {
    const steps = [
        { icon: FileText, t: "Upload", d: "Drag any tender PDF in", color: "teal" },
        { icon: Brain, t: "AI Reads", d: "Domain models scan every clause", color: "gold" },
        { icon: ClipboardCheck, t: "Insights", d: "Compliance & risk scoring", color: "teal" },
        { icon: Award, t: "Win", d: "Bid with full confidence", color: "gold" },
    ];

    return (
        <section className="py-28 lg:py-36 relative">
            <div className="container-tight">
                <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <motion.div {...fadeUp}>
                        <p className="badge-pill border border-teal-500/30 bg-teal-500/10 text-teal-300">
                            <Brain className="h-3 w-3" /> AI Engine
                        </p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                            Watch AI{" "}
                            <span className="gradient-text-gold">decode</span> a tender
                            in <span className="gradient-text-teal">47s</span>
                        </h2>
                        <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                            From upload to a fully structured report — our pipeline reads,
                            classifies, scores and translates every section. No more
                            70-page PDFs at 2 AM the night before a deadline.
                        </p>

                        <div className="mt-10 space-y-4">
                            {steps.map((s, i) => (
                                <motion.div
                                    key={s.t}
                                    {...fadeUp}
                                    transition={{ delay: i * 0.1 }}
                                    className="flex items-center gap-5"
                                >
                                    <div
                                        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
                                            s.color === "gold"
                                                ? "bg-gold/10 text-gold border border-gold/30"
                                                : "bg-teal-500/10 text-teal-300 border border-teal-500/30"
                                        }`}
                                    >
                                        <s.icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="font-serif text-xl font-bold text-white">
                                            {s.t}
                                        </p>
                                        <p className="text-sm text-slate-400">{s.d}</p>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div className="ml-auto h-px w-12 bg-gradient-to-r from-white/10 to-transparent" />
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        <Link to="/signup" className="mt-10 inline-block">
                            <button className="luxury-btn px-6 h-12 rounded-xl text-sm font-bold uppercase tracking-wider">
                                Try It Free
                                <ArrowRight className="ml-2 h-4 w-4 inline" />
                            </button>
                        </Link>
                    </motion.div>

                    {/* Live AI snippet */}
                    <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
                        <div className="rounded-3xl bg-[#0A1F35] border border-white/10 overflow-hidden shadow-2xl">
                            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5">
                                <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                                <span className="ml-3 font-mono text-xs text-slate-500">
                                    tender-analysis.engine
                                </span>
                                <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-mono text-teal-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
                                    Live
                                </span>
                            </div>
                            <div className="p-6 space-y-3 font-mono text-[12.5px] leading-7">
                                <div className="text-slate-500">
                                    {">"} Initializing tender extractor v4.2
                                </div>
                                <div className="text-teal-300">
                                    ✓ Parsed 87 pages, 14 schedules
                                </div>
                                <div className="text-teal-300">
                                    ✓ Detected EMD: ₹48,50,000 (Bank Guarantee)
                                </div>
                                <div className="text-teal-300">
                                    ✓ Eligibility verified — Match 87/100
                                </div>
                                <div className="text-amber-400">
                                    ⚠ Liquidated damages clause flagged
                                </div>
                                <div className="text-teal-300">
                                    ✓ All-language summary generated
                                </div>
                                <div className="text-slate-500">
                                    {">"} Report ready in 47.2s
                                </div>
                                <div className="pt-3 mt-3 border-t border-white/5 flex items-center justify-between">
                                    <span className="text-slate-400">Risk score</span>
                                    <span className="text-emerald-400 font-bold">
                                        LOW · 12/100
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   5 & 6. USE CASES — GOV + PRIVATE
   ============================================================ */
function UseCases() {
    return (
        <section id="use-cases" className="py-28 lg:py-36 relative">
            <div className="container-tight">
                <motion.div {...fadeUp} className="max-w-3xl mb-16">
                    <p className="badge-pill border border-gold/30 bg-gold/5 text-gold">
                        <Users className="h-3 w-3" /> Built for both sides
                    </p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                        For every kind of <span className="gradient-text-gold">bidder.</span>
                    </h2>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-6">
                    <UseCaseCard
                        accent="teal"
                        id="government"
                        Icon={Landmark}
                        title={
                            <>
                                For <span className="gradient-text-teal">Government</span>
                                <br />
                                Contractors
                            </>
                        }
                        tagline="Win more contracts with faster analysis, better compliance, and reduced risk."
                        bullets={[
                            { icon: Search, t: "Quickly assess eligibility and compliance" },
                            { icon: ShieldCheck, t: "Identify risks and red flags early" },
                            { icon: BarChart3, t: "Save time and improve win probability" },
                            { icon: Users, t: "Collaborate across your team securely" },
                        ]}
                    />
                    <UseCaseCard
                        accent="gold"
                        id="private"
                        Icon={Building2}
                        title={
                            <>
                                For <span className="gradient-text-gold">Private</span>
                                <br />
                                Bidders
                            </>
                        }
                        tagline="Make smarter bidding decisions and pursue the right opportunities."
                        bullets={[
                            { icon: Target, t: "Find the right tenders faster" },
                            { icon: ClipboardCheck, t: "Understand requirements with AI precision" },
                            { icon: DollarSign, t: "Optimize bids and improve ROI" },
                            { icon: Clock, t: "Reduce manual effort and turnaround time" },
                        ]}
                    />
                </div>

                {/* trust strip */}
                <motion.div
                    {...fadeUp}
                    className="mt-10 rounded-3xl glass-card p-6 lg:p-7 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6"
                >
                    {[
                        { Icon: Sparkles, t: "AI-Powered Analysis" },
                        { Icon: Lock, t: "Secure & Confidential" },
                        { Icon: ShieldCheck, t: "Enterprise-Grade Security" },
                        { Icon: BadgeCheck, t: "Trusted by Professionals" },
                    ].map(({ Icon, t }) => (
                        <div key={t} className="flex items-center gap-3">
                            <div className="h-10 w-10 shrink-0 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
                                <Icon className="h-4 w-4 text-teal-300" />
                            </div>
                            <p className="text-sm font-semibold text-white">{t}</p>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

function UseCaseCard({ accent, Icon, title, tagline, bullets, id }) {
    const isGold = accent === "gold";
    const ring = isGold ? "ring-gold/30" : "ring-teal-500/30";
    const iconBg = isGold
        ? "bg-gradient-to-br from-[#f5d76e] to-[#b8941f] text-[#071A2F]"
        : "bg-gradient-to-br from-[#0EA5A4] to-[#0F766E] text-white";
    const accentLine = isGold
        ? "from-gold/40 to-transparent"
        : "from-teal-400/40 to-transparent";
    const cardBg = isGold
        ? "bg-gradient-to-br from-[#fdf4d6] via-white to-[#fff9e9]"
        : "bg-gradient-to-br from-[#e6fafa] via-white to-[#dff3f1]";
    return (
        <motion.div
            id={id}
            {...fadeUp}
            className={`relative rounded-[28px] p-8 lg:p-10 ${cardBg} ring-1 ${ring} shadow-xl overflow-hidden`}
        >
            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accentLine}`} />
            <div className="flex flex-col items-center text-center">
                <div className={`h-20 w-20 rounded-full ${iconBg} flex items-center justify-center shadow-lg`}>
                    <Icon className="h-9 w-9" />
                </div>
                <h3 className="mt-6 font-serif text-3xl lg:text-4xl font-bold uppercase leading-[1.05] text-slate-900">
                    {title}
                </h3>
                <div className={`mt-4 h-px w-12 bg-gradient-to-r ${isGold ? "from-gold" : "from-teal-500"} to-transparent`} />
                <p className="mt-4 text-slate-600 max-w-md">{tagline}</p>
            </div>

            <ul className="mt-8 space-y-5">
                {bullets.map((b) => (
                    <li key={b.t} className="flex items-center gap-4">
                        <div
                            className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${
                                isGold
                                    ? "bg-gold/15 text-[#a07d0e]"
                                    : "bg-teal-500/15 text-[#0F766E]"
                            }`}
                        >
                            <b.icon className="h-5 w-5" />
                        </div>
                        <span className="text-[15px] font-medium text-slate-800 leading-snug">
                            {b.t}
                        </span>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
}

/* ============================================================
   7. SECURITY & COMPLIANCE
   ============================================================ */
function Security() {
    const items = [
        { label: "Bank-Level Security", sub: "256-bit SSL Encryption", Icon: Lock },
        { label: "SOC 2 Type II", sub: "Audited annually", Icon: ShieldCheck },
        { label: "GDPR", sub: "Fully compliant", Icon: BadgeCheck },
        { label: "DPDP Act 2023", sub: "India-ready", Icon: Award },
        { label: "ISO 27001", sub: "Hosted in India", Icon: Network },
        { label: "Zero-Knowledge Storage", sub: "Your data, your control", Icon: Layers },
    ];
    return (
        <section className="py-24 lg:py-32 relative border-y border-white/5 bg-[#06172A]">
            <div className="container-tight">
                <motion.div {...fadeUp} className="max-w-3xl">
                    <p className="badge-pill border border-teal-500/30 bg-teal-500/10 text-teal-300">
                        <ShieldCheck className="h-3 w-3" /> Security & Compliance
                    </p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                        Enterprise-grade <span className="gradient-text-teal">trust.</span>
                    </h2>
                    <p className="mt-5 text-lg text-slate-300 max-w-2xl">
                        Your tenders contain commercially sensitive information. We treat
                        them with the same care a bank treats its vault.
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {items.map((it) => (
                        <motion.div
                            key={it.label}
                            {...fadeUp}
                            className="gradient-border p-6 flex items-start gap-4"
                        >
                            <div className="h-11 w-11 shrink-0 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-300">
                                <it.Icon className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="font-serif text-lg font-bold text-white">
                                    {it.label}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">{it.sub}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   8. TESTIMONIALS
   ============================================================ */
function Testimonials() {
    return (
        <section className="py-28 lg:py-36">
            <div className="container-tight">
                <motion.div {...fadeUp} className="max-w-3xl mb-16">
                    <p className="badge-pill border border-gold/30 bg-gold/5 text-gold">
                        <Star className="h-3 w-3" /> Voices from the field
                    </p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                        Loved by bid teams
                        <br />
                        <span className="gradient-text-gold">across India.</span>
                    </h2>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-5">
                    {TESTIMONIALS.map((t, i) => (
                        <motion.div
                            key={t.name}
                            {...fadeUp}
                            transition={{ delay: i * 0.1, duration: 0.7 }}
                            className="gradient-border p-8"
                        >
                            <div className="flex items-center gap-1 mb-5">
                                {STAR_KEYS.map((s) => (
                                    <Star
                                        key={s}
                                        className="h-4 w-4 fill-gold text-gold"
                                    />
                                ))}
                            </div>
                            <p className="font-serif text-lg text-white leading-relaxed">
                                {`"${t.quote}"`}
                            </p>
                            <div className="mt-7 pt-6 border-t border-white/10 flex items-center gap-3">
                                <img
                                    src={t.image}
                                    alt={t.name}
                                    className="h-11 w-11 rounded-full object-cover ring-2 ring-teal-500/30"
                                />
                                <div>
                                    <p className="font-bold text-sm text-white">{t.name}</p>
                                    <p className="text-xs text-slate-400">{t.role}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   9. PRICING
   ============================================================ */
function Pricing() {
    return (
        <section id="pricing" className="py-28 lg:py-36 relative">
            <div className="absolute inset-0 mesh-hero opacity-40" />
            <div className="container-tight relative">
                <motion.div {...fadeUp} className="max-w-3xl mb-16 text-center mx-auto">
                    <p className="badge-pill border border-teal-500/30 bg-teal-500/10 text-teal-300 mx-auto">
                        <Coins className="h-3 w-3" /> Simple pricing
                    </p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                        Pricing that <span className="gradient-text-gold">scales</span> with
                        you.
                    </h2>
                    <p className="mt-5 text-lg text-slate-300">
                        Monthly billing. Cancel anytime. 1 tender analysis = 1 credit.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.id}
                            {...fadeUp}
                            transition={{ delay: i * 0.08, duration: 0.7 }}
                            className={`relative rounded-[28px] p-8 lg:p-9 flex flex-col ${
                                p.popular
                                    ? "lg:-translate-y-4 bg-gradient-to-b from-[#0F766E] to-[#0E5F58] ring-2 ring-gold/60 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]"
                                    : "gradient-border"
                            }`}
                            data-testid={`pricing-card-${p.id}`}
                        >
                            {p.popular && (
                                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                                    <span className="badge-pill bg-gold text-[#071A2F] shadow-lg">
                                        <Sparkles className="h-3 w-3" /> Most Popular
                                    </span>
                                </div>
                            )}
                            <h3 className="font-serif text-2xl font-bold text-white">
                                {p.name}
                            </h3>
                            <p
                                className={`mt-1 text-sm ${p.popular ? "text-teal-100" : "text-slate-400"}`}
                            >
                                {p.tagline}
                            </p>
                            <div className="mt-6 flex items-baseline gap-1">
                                <span className="font-serif text-5xl font-bold text-white">
                                    ₹{p.price}
                                </span>
                                <span
                                    className={`text-sm ${p.popular ? "text-teal-100" : "text-slate-400"}`}
                                >
                                    /month
                                </span>
                            </div>
                            <div
                                className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold w-fit ${
                                    p.popular
                                        ? "bg-gold/20 text-gold border border-gold/40"
                                        : "bg-teal-500/10 text-teal-300 border border-teal-500/30"
                                }`}
                            >
                                <Sparkles className="h-3 w-3" />
                                {p.credits}
                            </div>
                            <ul className="mt-7 space-y-3 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5">
                                        <CheckCircle2
                                            className={`h-4 w-4 mt-0.5 shrink-0 ${
                                                p.popular ? "text-gold" : "text-teal-400"
                                            }`}
                                        />
                                        <span className="text-sm text-slate-200">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup" className="mt-8 block">
                                <button
                                    className={`w-full h-12 rounded-2xl font-bold text-sm uppercase tracking-wider ${
                                        p.popular
                                            ? "luxury-btn-gold"
                                            : "border border-white/15 text-white hover:bg-white/5 transition-colors"
                                    }`}
                                    data-testid={`pricing-cta-${p.id}`}
                                >
                                    {p.cta}
                                </button>
                            </Link>
                        </motion.div>
                    ))}
                </div>
                <p className="mt-10 text-center text-sm text-slate-500">
                    No hidden charges. Cancel anytime. GST extra as applicable.
                </p>
            </div>
        </section>
    );
}

/* ============================================================
   10. FAQ
   ============================================================ */
function FaqSection() {
    return (
        <section className="py-28 lg:py-36 relative">
            <div className="container-tight max-w-3xl">
                <motion.div {...fadeUp} className="text-center mb-14">
                    <p className="badge-pill border border-gold/30 bg-gold/5 text-gold mx-auto">
                        FAQ
                    </p>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05]">
                        Questions, <span className="gradient-text-teal">answered.</span>
                    </h2>
                </motion.div>
                <Accordion type="single" collapsible className="space-y-3">
                    {FAQS.slice(0, 7).map((f, i) => (
                        <AccordionItem
                            key={f.q}
                            value={`item-${i}`}
                            className="gradient-border px-6 border-0 data-[state=open]:bg-[#0F2D44]/60"
                        >
                            <AccordionTrigger
                                className="text-left text-base font-bold text-white hover:no-underline py-5"
                                data-testid={`faq-trigger-${i}`}
                            >
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-300 leading-relaxed pb-5">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

/* ============================================================
   FINAL CTA
   ============================================================ */
function FinalCTA() {
    return (
        <section className="py-24 lg:py-28">
            <div className="container-tight">
                <div className="relative rounded-[32px] overflow-hidden p-12 lg:p-20 text-center bg-gradient-to-br from-[#0F766E] via-[#0E6760] to-[#0A4842]">
                    <div className="absolute inset-0 grid-pattern-dark opacity-30" />
                    <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold/20 blur-3xl pulse-glow" />
                    <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-teal-400/20 blur-3xl pulse-glow" />

                    <div className="relative max-w-3xl mx-auto">
                        <p className="badge-pill border border-gold/40 bg-gold/10 text-gold mx-auto">
                            <Sparkles className="h-3 w-3" /> Start in 30 seconds
                        </p>
                        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase leading-[1.05] text-balance">
                            Bid smarter,
                            <br />
                            <span className="gradient-text-gold">win more often.</span>
                        </h2>
                        <p className="mt-6 text-lg text-teal-50 max-w-xl mx-auto">
                            Join 1,200+ Indian businesses that trust TenderTrackAI to
                            decode every tender before they bid.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/signup">
                                <button
                                    className="luxury-btn-gold px-8 h-14 rounded-2xl text-sm font-bold uppercase tracking-[0.15em] w-full sm:w-auto inline-flex items-center justify-center gap-3"
                                    data-testid="final-cta-signup-btn"
                                >
                                    Sign Up Free
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </Link>
                            <Link to="/pricing">
                                <button className="px-8 h-14 rounded-2xl text-sm font-bold uppercase tracking-[0.15em] border border-white/30 bg-white/10 hover:bg-white/15 text-white w-full sm:w-auto">
                                    View Pricing
                                </button>
                            </Link>
                        </div>
                        <p className="mt-6 text-xs text-teal-100/80">
                            <TrendingUp className="inline h-3 w-3 mr-1" />
                            No credit card · 2 free analyses · Cancel anytime
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   HOME PAGE
   ============================================================ */
export default function Home() {
    return (
        <div className="bg-[#071A2F] text-white overflow-hidden">
            <Hero />
            <TrustedBy />
            <FeaturesBento />
            <AIDemo />
            <UseCases />
            <Security />
            <Testimonials />
            <Pricing />
            <FaqSection />
            <FinalCTA />
        </div>
    );
}

// Re-export so /pricing route still works
export function PricingSection() {
    return <Pricing />;
}
